import React, { useMemo } from "react";
import { CCAvatar } from "@pymblecitizencoders/cc-ui";
import { Info } from "lucide-react";
import { Tooltip } from "../common/Tooltip";
import {
	PublishRoleHolder,
	PublishStep,
	usePublishRoles,
} from "../../hooks/useEventPublish";
import { useProfileImages } from "../../hooks/useProfileImages";
import { personName } from "../../utils/personName";
import { formatWhen } from "../../utils/eventWhen";
import "./eventManager.css";

interface PublishChainProps {
	steps: PublishStep[];
	/** The step the event is sitting at, or null when it is not in review. */
	currentStep?: number | null;
	/** Only an event in review HAS a current step; a declined one stopped. */
	inReview: boolean;
	/**
	 * Narrow rendering for the editor sidebar, which is roughly a third the
	 * width of the sign-off screen's panel. The pill and timestamp move under
	 * the name instead of sitting in their own right-hand column, which is what
	 * stopped every row wrapping into four lines at that width.
	 */
	compact?: boolean;
}

/** What one row of the chain says, once the person and the state are known. */
interface Line {
	/** approved | declined | changes | current | waiting — drives the colour. */
	state: string;
	pill: string;
	/** The person acting, when anyone knows who that is. */
	who?: string;
	/** The id the photo is filed under, when it is one that has photos. */
	photoId?: string;
	/** The role, which is what the step is really about. */
	role: string;
	meta?: string;
	reason?: string | null;
	/** Everyone who could take this step. Empty on a decided one — that step
	    has an answer, and the person who gave it is the only one that matters. */
	people: PublishRoleHolder[];
	/** No live holder at all — a step that nobody can currently decide. */
	unheld?: boolean;
}

/** Faces shown before the rest become a "+n". */
const MAX_FACES = 3;

/** The hover's heading: who this step is with, or that nobody holds it. */
const peopleLabel = (line: Line) =>
	line.people.length
		? `${line.role}: ${line.people.length} ${line.people.length === 1 ? "holder" : "holders"}`
		: line.unheld
			? `${line.role}: nobody holds this role`
			: `${line.who ?? line.role}, ${line.role}`;

/** The pill wording, matching the tabs and the list's status column. */
const PILL: Record<string, string> = {
	approved: "Approved",
	declined: "Declined",
	changes: "Changes",
	current: "Pending",
	waiting: "Pending",
};

/** The pill and dot palette, reusing the status classes the list already has. */
const TONE: Record<string, string> = {
	approved: "published",
	declined: "declined",
	changes: "changes_requested",
	current: "in_review",
	waiting: "none",
};

/**
 * The approval chain, drawn as a timeline: who, in what order, and where it is
 * up to.
 *
 * Shared by the sign-off screen's panel and the editor's Sign-off tab. They had
 * two copies of this list and the copies had already diverged in what they said
 * about a step nobody had reached yet, which is precisely the thing someone
 * opens either screen to find out.
 *
 * The people come from two places, because the workflow only records a name
 * once a decision is made. A decided step names its decider; an undecided one
 * is answered from the role's live holders, which is the honest answer to "who
 * is this waiting on" and the question the mockup's avatars are asking. Where
 * neither is known the row falls back to the role, so it is never blank.
 */
export const PublishChain: React.FC<PublishChainProps> = ({
	steps,
	currentStep,
	inReview,
	compact = false,
}) => {
	const { imageUrl } = useProfileImages();
	/* One cached fetch for the whole screen, whichever rows are on it. Readable
	   by any active staff member, so an approver who is not a role administrator
	   still sees who they are waiting on. */
	const { roles } = usePublishRoles();

	const holders = useMemo(() => {
		const map = new Map<string, PublishRoleHolder[]>();
		for (const role of roles) {
			map.set(
				role.Code,
				(role.Holders ?? []).filter((h) => h.Live),
			);
		}
		return map;
	}, [roles]);

	/** Roles worked out per event, which have no holders by design. */
	const derivedRoles = useMemo(
		() => new Set(roles.filter((r) => r.Derived).map((r) => r.Code)),
		[roles],
	);

	const lines: Line[] = steps.map((step, i) => {
		const decided = step.Decision ?? null;
		const current = inReview && currentStep === i;
		const state = decided ?? (current ? "current" : "waiting");
		const live = holders.get(step.RoleId) ?? [];
		const role = step.Name || step.RoleId;

		if (decided) {
			/* The decider is usually a holder of the step's own role, and the
			   holder record carries the ContactPersonal id photos are filed
			   under. The stamp on the step may not: older ones hold a
			   `School.Contact` id, which no photo answers to. Matching by name
			   recovers the face for those. */
			const asHolder = live.find(
				(h) =>
					personName(h.Name) === personName(step.DecidedBy?.Name) ||
					personName(h.DelegateName) === personName(step.DecidedBy?.Name),
			);
			return {
				state,
				pill: PILL[state] ?? "Decided",
				who: personName(step.DecidedBy?.Name) || undefined,
				/* A ContactPersonal id, which is what photos are filed under —
				   so a decided step shows the decider's face like an undecided
				   one shows its holder's. It used to be a `School.Contact._id`
				   and always fell through to initials; the API now stamps the
				   id space the photo container actually uses. */
				photoId:
					asHolder?.ContactId ??
					(step.DecidedBy?._id ? String(step.DecidedBy._id) : undefined),
				role,
				meta: step.DecidedAt ? formatWhen(step.DecidedAt) : undefined,
				reason: step.Reason,
				people: [],
			};
		}

		/* A derived step names its person once the event is submitted, and the
		   name on the step is the answer, not the role's (empty) holder list. */
		if (step.ApproverName) {
			return {
				state,
				pill: PILL[state],
				who: personName(step.ApproverName) || undefined,
				photoId: step.ApproverId ?? undefined,
				role,
				meta: current ? "Awaiting response" : "Not started",
				people: [],
			};
		}

		const first = live[0];
		return {
			state,
			pill: PILL[state],
			/* Named after the first and counted after that. Three full names in a
			   360px sidebar wrap to three lines and bury the role underneath;
			   the faces beside them carry how many, and the hover carries who. */
			who:
				live.length > 1
					? `${personName(first?.DelegateName || first?.Name) || role} and ${live.length - 1} other${live.length > 2 ? "s" : ""}`
					: personName(first?.DelegateName || first?.Name) || undefined,
			photoId: first?.DelegateTo || first?.ContactId,
			role,
			/* Nobody at all is worth saying, since the step cannot be decided.
			   A holder whose name did not resolve is NOT that, and saying so
			   put "No one holds this role" under three faces that were plainly
			   somebody. Unheld is the only case that gets the sentence. */
			unheld: live.length === 0 && !derivedRoles.has(step.RoleId),
			meta: current ? "Awaiting response" : "Not started",
			people: live,
		};
	});

	return (
		<div className={compact ? "em-chain em-chain--compact" : "em-chain"}>
			{lines.map((line, i) => (
				<div
					key={`${steps[i].RoleId}-${steps[i].Order}`}
					className={`em-chain__row is-${TONE[line.state] ?? "none"}`}
				>
					{/* The rail is drawn by the row, so the connecting line never
					    outlives the last step or reaches above the first. */}
					<span className="em-chain__rail" aria-hidden="true">
						<span className="em-chain__dot" />
					</span>
					{/* A face each for the people who could take this step, and their
					    names on hover. A step waiting on three people is waiting on
					    three people; showing one of them and "+2 more" left the other
					    two anonymous until somebody went looking for the role. */}
					<Tooltip
						label={peopleLabel(line)}
						details={
							line.people.length ? (
								<>
									{line.people.map((h) => (
										<span key={h._id}>
											{personName(h.DelegateName || h.Name) || h.ContactId}
											{h.Title ? ` — ${h.Title}` : ""}
											{h.DelegateName ? " (delegate)" : ""}
										</span>
									))}
								</>
							) : undefined
						}
						align="start"
						className="em-chain__faces-wrap"
					>
						<span className="em-chain__faces">
							{line.people.length === 0 ? (
								<CCAvatar
									src={imageUrl(line.photoId)}
									name={line.who || line.role}
									/* Matches the avatar column each variant reserves. */
									size={compact ? 28 : 32}
									decorative
								/>
							) : (
								line.people
									.slice(0, MAX_FACES)
									.map((h) => (
										<CCAvatar
											key={h._id}
											src={imageUrl(h.DelegateTo || h.ContactId)}
											name={personName(h.DelegateName || h.Name) || line.role}
											size={compact ? 28 : 32}
											decorative
										/>
									))
							)}
							{line.people.length > MAX_FACES && (
								<span className="em-chain__faces-more">
									+{line.people.length - MAX_FACES}
								</span>
							)}
						</span>
					</Tooltip>
					<span className="em-chain__body">
						{/* The words are a second trigger for the faces' hover: they
						    state the same fact — "and 3 others" is the same three
						    people the stack is showing — so reaching for either
						    should answer it. `interactive` keeps the wrapper out of
						    `aria-hidden`, or the name and role would leave the
						    accessibility tree with only the tooltip's summary left. */}
						<Tooltip
							label={peopleLabel(line)}
							details={
								line.people.length ? (
									<>
										{line.people.map((h) => (
											<span key={h._id}>
												{personName(h.DelegateName || h.Name) || h.ContactId}
												{h.Title ? ` — ${h.Title}` : ""}
												{h.DelegateName ? " (delegate)" : ""}
											</span>
										))}
									</>
								) : undefined
							}
							align="start"
							interactive
							className="em-chain__names"
						>
							<span className="em-chain__names-inner">
								<span className="em-chain__who">{line.who || line.role}</span>
								{/* The second line is the role when the first is a person.
								    With no person to name it either warns that the step is
								    unheld, or says nothing at all rather than repeating the
								    role back. */}
								{(line.who || line.unheld) && (
									<span
										className={`em-chain__role${line.unheld ? " is-unheld" : ""}`}
									>
										{line.who
											? line.role
											: line.unheld
												? "No one holds this role"
												: ""}
									</span>
								)}
							</span>
						</Tooltip>
						{/* The note the decider left, on hover rather than in the row.
						    
						    Inline it set the row's height by however much prose
						    somebody typed, in a column 360px wide — and it is read
						    once, acted on, and then in the way. The trigger says a
						    note exists; `interactive` and a native `title` keep it
						    reachable by keyboard and on a touch device. */}
						{line.reason && (
							<Tooltip
								label={`${line.who ? `${line.who}, ` : ""}${line.role}`}
								details={<span className="em-tip__desc">{line.reason}</span>}
								align="start"
								interactive
								className="em-chain__note-wrap"
							>
								<button
									type="button"
									className="em-chain__note"
									title={line.reason}
								>
									<Info size={12} aria-hidden="true" />
									Note
								</button>
							</Tooltip>
						)}
					</span>
					<span className="em-chain__meta">
						<span className={`em-approve-status is-${TONE[line.state]}`}>
							{line.pill}
						</span>
						{line.meta && <span className="em-chain__when">{line.meta}</span>}
					</span>
				</div>
			))}
		</div>
	);
};
